import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaSpinner,
  FaUserShield,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCrown,
  FaEye,
  FaEdit,
  FaTrash,
  FaIdCard,
  FaFilter,
  FaCalendar,
} from "react-icons/fa";

const Admins = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const response = await axiosInstance.get(API.ADMIN.ADMIN_LIST, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setAdmins(response.data.admins);
        setFilteredAdmins(response.data.admins);
      } catch (error) {
        console.error("Error fetching admins:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAdmins();
  }, []);

  const handleFilter = () => {
    if (!startDate || !endDate) {
      alert("Please select valid date ranges to filter!");
      return;
    }

    const filteredData = admins.filter((admin) => {
      const createdAt = new Date(admin.createdAt);
      // Set end date to end of day for proper comparison
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      return createdAt >= startDate && createdAt <= endOfDay;
    });

    setFilteredAdmins(filteredData);
  };

  const handleDelete = async (adminId) => {
    try {
      await axiosInstance.delete(`${API.ADMIN.ADMIN_DELETE}/${adminId}`);
      setAdmins(admins.filter((admin) => admin._id !== adminId));
      setFilteredAdmins(
        filteredAdmins.filter((admin) => admin._id !== adminId)
      );
    } catch (error) {
      console.error("Error deleting admin:", error);
    }
  };

  // Show loading state for the entire page
  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUserShield className="text-6xl text-blue-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-blue-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Loading Admin Data
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch admin information...
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Admin Management</h2>
        <button
          onClick={() => navigate("/admins/add")}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md"
        >
          + Add Admin
        </button>
      </div>

      {/* Enhanced Filter Section */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl shadow-lg mb-6 p-6 border border-orange-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaCalendar className="mr-2 text-orange-500" />
              Start Date
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select start date"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-white"
              maxDate={new Date()}
            />
          </div>

          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaCalendar className="mr-2 text-orange-500" />
              End Date
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select end date"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-white"
              maxDate={new Date()}
              minDate={startDate}
            />
          </div>

          {/* Enhanced Apply Filter Button */}
          <button
            className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 flex items-center font-medium"
            onClick={handleFilter}
          >
            <FaFilter className="mr-2" />
            Apply Filter
          </button>
        </div>
      </div>

      {/* Admins List */}
      <div className="bg-white rounded-lg shadow">
        {filteredAdmins.length === 0 ? (
          <div className="text-center py-12">
            <FaUserShield className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-500">No admins found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role & Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAdmins.map((admin) => (
                  <tr key={admin._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center mb-1">
                          <FaUser className="text-blue-500 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {admin.fullName}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 flex items-center">
                          <FaIdCard className="mr-1" />
                          ID: {admin.employeeCode}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center">
                          <FaCrown className="text-yellow-500 mr-2" />
                          <span className="text-sm font-medium text-gray-900 bg-yellow-100 px-2 py-1 rounded-full">
                            {admin.role}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <FaPhone className="text-green-500 mr-2" />
                          <span className="text-sm text-gray-900">
                            {admin.phone}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <FaEnvelope className="text-purple-500 mr-2" />
                          <span className="text-sm text-gray-600">
                            {admin.emailId}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <Link
                          to={`/admins/detail/${admin._id}`}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-150"
                          title="View Details"
                        >
                          <FaEye />
                        </Link>
                        <Link
                          to={`/admins/edit/${admin._id}`}
                          className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50 transition-colors duration-150"
                          title="Edit Admin"
                        >
                          <FaEdit />
                        </Link>
                        <button
                          onClick={() => handleDelete(admin._id)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors duration-150"
                          title="Delete Admin"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admins;
